import React from 'react';
import { ArrowRight, ArrowLeft, Check, Camera, Video, Play, Film, Users, Image, Sparkles, Building2, Briefcase, User, MapPin, Monitor, Megaphone, Mail, PresentationIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CookieBanner from '../components/layout/CookieBanner';

const trustPoints = [
  'High-end, cinematic quality',
  'Business & brand focused',
  'Optimized for ads, web & social',
  'Full production, start to finish'
];

const whoWeWorkWith = [
  { icon: Building2, title: 'Businesses & Brands' },
  { icon: Briefcase, title: 'Service-Based Companies' },
  { icon: Sparkles, title: 'Startups & SaaS' },
  { icon: User, title: 'Personal Brands & Influencers' },
  { icon: MapPin, title: 'Local & National Companies' }
];

const contentBenefits = [
  'Grabs attention instantly',
  'Tells a clear brand story',
  'Builds credibility and authority',
  'Drives engagement and conversions'
];

const videographyServices = [
  {
    icon: Film,
    title: 'Brand & Storytelling Videos',
    description: 'Cinematic brand videos that showcase who you are and what makes you different.'
  },
  {
    icon: Megaphone,
    title: 'Promotional & Marketing Videos',
    description: 'High-impact videos designed for ads, websites, and landing pages.'
  },
  {
    icon: Play,
    title: 'Social Media Video Content',
    description: 'Short-form, vertical videos optimized for Instagram, TikTok, YouTube Shorts, and ads.'
  },
  {
    icon: Building2,
    title: 'Corporate & Professional Videos',
    description: 'Polished videos for teams, training, events, and internal or external communication.'
  },
  {
    icon: Users,
    title: 'Testimonial & Interview Videos',
    description: 'Authentic customer stories that build trust and drive conversions.'
  }
];

const photographyServices = [
  {
    icon: Camera,
    title: 'Brand Photography',
    description: 'Professional images that reflect your brand, personality, and message.'
  },
  {
    icon: Image,
    title: 'Product Photography',
    description: 'Clean, high-quality photos designed to sell and convert.'
  },
  {
    icon: Sparkles,
    title: 'Lifestyle Photography',
    description: 'Natural, engaging images that connect your brand to real-world use.'
  },
  {
    icon: User,
    title: 'Corporate Headshots',
    description: 'Modern, professional headshots for teams and leadership.'
  },
  {
    icon: Users,
    title: 'Event Photography',
    description: 'Capture key moments with clarity, emotion, and professionalism.'
  }
];

const whyChooseUs = [
  'Cinematic, high-end production quality',
  'Creative direction tailored to your brand',
  'Content optimized for marketing & ads',
  'Fast turnaround times',
  'Easy, professional process'
];

const process = [
  {
    step: '01',
    title: 'Discovery & Creative Direction',
    description: 'We understand your goals, audience, and vision.'
  },
  {
    step: '02',
    title: 'Planning & Pre-Production',
    description: 'We map out shots, locations, and messaging before filming.'
  },
  {
    step: '03',
    title: 'Production',
    description: 'Professional filming and photography with top-tier equipment.'
  },
  {
    step: '04',
    title: 'Editing & Post-Production',
    description: 'High-quality editing, color grading, and sound design.'
  },
  {
    step: '05',
    title: 'Delivery & Optimization',
    description: 'Content delivered in formats optimized for web, ads, and social platforms.'
  }
];

const whereContentWorks = [
  { icon: Monitor, label: 'Websites & landing pages' },
  { icon: Megaphone, label: 'Paid advertising campaigns' },
  { icon: Play, label: 'Social media platforms' },
  { icon: Mail, label: 'Email marketing' },
  { icon: PresentationIcon, label: 'Sales presentations' }
];

const results = [
  'Stronger brand perception',
  'Higher engagement & watch time',
  'Increased trust and credibility',
  'Content that converts better in ads and sales funnels'
];

const MediaProduction = () => {
  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[85vh] flex flex-col justify-center overflow-hidden bg-[#f8f7f4] pt-32 pb-16">
          {/* Gradient blob background */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div
              className="absolute top-0 right-0 w-[80%] h-[120%] opacity-50"
              style={{
                background: `
                  radial-gradient(ellipse 70% 50% at 75% 20%, rgba(251, 146, 60, 0.3) 0%, transparent 50%),
                  radial-gradient(ellipse 60% 50% at 85% 50%, rgba(217, 70, 239, 0.25) 0%, transparent 50%),
                  radial-gradient(ellipse 50% 60% at 65% 70%, rgba(244, 63, 94, 0.2) 0%, transparent 50%)
                `,
                filter: 'blur(60px)',
                transform: 'rotate(-10deg)'
              }}
            />
          </div>

          <div className="relative z-10 px-6 md:px-12 lg:px-24">
            {/* Back link */}
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-gray-600 hover:text-black transition-colors mb-12"
            >
              <ArrowLeft size={16} /> Back to Home
            </Link>

            <div className="max-w-4xl">
              <span className="text-sm uppercase tracking-widest text-gray-500 mb-4 block">Videography & Photography</span>
              <h1 className="text-[clamp(2.5rem,5vw,4.5rem)] font-light leading-[1.1] text-black mb-6">
                Visual Content That Captures Attention —{' '}
                <span className="font-medium">And Converts</span>
              </h1>
              <p className="text-xl text-gray-700 max-w-2xl mb-4">
                Professional videography and photography that tells your story, elevates your brand, and drives results.
              </p>
              <p className="text-lg text-gray-600 max-w-2xl mb-8">
                From cinematic videos to high-impact photography, we create visual content designed to stand out, 
                build trust, and perform across websites, ads, and social media.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white rounded-full font-medium hover:bg-gray-800 transition-colors"
                >
                  Book a Free Consultation <ArrowRight size={18} />
                </a>
                <Link
                  to="/#work"
                  className="inline-flex items-center gap-2 px-8 py-4 border border-black text-black rounded-full font-medium hover:bg-black hover:text-white transition-colors"
                >
                  View Our Portfolio
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Strip */}
        <section className="py-8 bg-[#0a0a0a] text-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="flex flex-wrap justify-center gap-8 md:gap-16">
              {trustPoints.map((point, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Check size={18} className="text-orange-400" />
                  <span className="text-sm md:text-base">{point}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Who We Work With */}
        <section className="py-24 bg-[#f8f7f4]">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                Who We Work With
              </h2>
              <p className="text-lg text-gray-600">
                We create professional visuals for brands that want to look credible, polished, and premium.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-12">
              {whoWeWorkWith.map((item, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 text-center shadow-sm hover:shadow-md transition-shadow">
                  <item.icon className="w-10 h-10 text-gray-700 mx-auto mb-4" />
                  <h3 className="text-sm font-medium text-black">{item.title}</h3>
                </div>
              ))}
            </div>

            <p className="text-center text-lg text-gray-700 font-medium">
              If your visuals don't reflect the quality of your business, we change that.
            </p>
          </div>
        </section>

        {/* What We Do */}
        <section className="py-24 bg-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                  Strategic Visual Content — Not Just Pretty Footage
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  Great visuals do more than look good. They communicate trust, value, and professionalism in seconds.
                </p>
                <div className="mb-8">
                  <p className="text-gray-700 font-medium mb-4">
                    We combine creative direction with business strategy to create content that:
                  </p>
                  <ul className="space-y-3">
                    {contentBenefits.map((item, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <Check size={18} className="text-orange-500 flex-shrink-0" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-lg text-black font-medium">
                  Every shoot is planned with purpose and performance in mind.
                </p>
              </div>
              <div className="relative">
                <div className="rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/vm4oytwp_240911_AmigoStudios_221-1200x800-9b9f185.webp" 
                    alt="Professional photo studio shoot with model and production crew"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Videography Services */}
        <section className="py-24 bg-[#f8f7f4]">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="mb-16">
              <h2 className="text-[clamp(3rem,8vw,8rem)] font-light text-gray-300 leading-none tracking-tight">
                <span className="block">VIDEO</span>
                <span className="block ml-[10%]">SERVICES</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {videographyServices.map((service, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-all group">
                  <service.icon className="w-10 h-10 text-gray-400 group-hover:text-orange-500 mb-4 transition-colors" />
                  <h3 className="text-xl font-medium text-black mb-3">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Photography Services */}
        <section className="py-24 bg-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="mb-16">
              <h2 className="text-[clamp(3rem,8vw,8rem)] font-light text-gray-300 leading-none tracking-tight">
                <span className="block">PHOTO</span>
                <span className="block ml-[10%]">SERVICES</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {photographyServices.map((service, index) => (
                <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-md transition-all group">
                  <service.icon className="w-10 h-10 text-gray-400 group-hover:text-rose-500 mb-4 transition-colors" />
                  <h3 className="text-xl font-medium text-black mb-3">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Photo Gallery */}
        <section className="py-24 bg-[#f8f7f4]">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                Our Photography Work
              </h2>
              <p className="text-lg text-gray-600">
                A glimpse into the professional photography we create for brands and businesses.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="group relative overflow-hidden rounded-2xl aspect-[3/4] col-span-1 row-span-1">
                <img 
                  src="https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/4k3efnqw_116262365_950165942157195_2379775665947469334_n-1920w.webp" 
                  alt="Professional photography - Portrait"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300"></div>
              </div>
              <div className="group relative overflow-hidden rounded-2xl aspect-[3/4] col-span-1 row-span-1">
                <img 
                  src="https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/scnrhqat_145709337_1096787837495004_7521943300078946188_n-1920w.webp" 
                  alt="Professional photography - Beauty"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300"></div>
              </div>
              <div className="group relative overflow-hidden rounded-2xl aspect-[3/4] col-span-1 row-span-1">
                <img 
                  src="https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/xrlcx931_141261280_1089052904935164_2504976619572815088_n-1920w.webp" 
                  alt="Professional photography - Fashion"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300"></div>
              </div>
              <div className="group relative overflow-hidden rounded-2xl aspect-[3/4] col-span-1 row-span-1">
                <img 
                  src="https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/1daw19zx_242133177_1249377668902686_3868742000902403401_n-1920w.webp" 
                  alt="Professional photography - Editorial"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300"></div>
              </div>
              <div className="group relative overflow-hidden rounded-2xl aspect-[3/4] col-span-1 row-span-1">
                <img 
                  src="https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/djanwlxh_173242215_1141103496396771_2781462771745734649_n-1920w.webp" 
                  alt="Professional photography - Commercial"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300"></div>
              </div>
              <div className="group relative overflow-hidden rounded-2xl aspect-[3/4] col-span-1 row-span-1">
                <img 
                  src="https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/j6ep7m9a_210663927_1196208160886304_5696225805014299632_n-1920w.webp" 
                  alt="Professional photography - Barber"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300"></div>
              </div>
              <div className="group relative overflow-hidden rounded-2xl aspect-[3/4] col-span-1 row-span-1">
                <img 
                  src="https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/z6yit2m9_273942618_1363464310827354_3684880552527655592_n-1920w.webp" 
                  alt="Professional photography - Medical Team"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300"></div>
              </div>
              <div className="group relative overflow-hidden rounded-2xl aspect-[3/4] col-span-1 row-span-1">
                <img 
                  src="https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/0k4xtdhf_278316211_1399105513929900_2340690694029605433_n-1920w.webp" 
                  alt="Professional photography - Musicians"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300"></div>
              </div>
              <div className="group relative overflow-hidden rounded-2xl aspect-[3/4] col-span-1 row-span-1">
                <img 
                  src="https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/wwfjrxs8_290383626_1451680725339045_44482263529675182_n-1920w.webp" 
                  alt="Professional photography - Event"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300"></div>
              </div>
              <div className="group relative overflow-hidden rounded-2xl aspect-[3/4] col-span-1 row-span-1">
                <img 
                  src="https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/e9w8loyw_328162905_867168901208089_6078024563938496894_n-1920w.webp" 
                  alt="Professional photography - Business"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300"></div>
              </div>
              <div className="group relative overflow-hidden rounded-2xl aspect-[3/4] col-span-1 row-span-1">
                <img 
                  src="https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/pffvgwg6_DSC01324.jpg" 
                  alt="Professional photography - Studio Portrait"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300"></div>
              </div>
              <div className="group relative overflow-hidden rounded-2xl aspect-[3/4] col-span-1 row-span-1">
                <img 
                  src="https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/ja18s3vp_DSC04919.jpeg" 
                  alt="Professional photography - Lifestyle"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300"></div>
              </div>
              <div className="group relative overflow-hidden rounded-2xl aspect-[3/4] col-span-1 row-span-1">
                <img 
                  src="https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/5ltvyptj_DSC06318.jpeg" 
                  alt="Professional photography - Creative"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300"></div>
              </div>
              <div className="group relative overflow-hidden rounded-2xl aspect-[3/4] col-span-1 row-span-1">
                <img 
                  src="https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/170f8ixb_DJI_20251208182839_0193_D.jpg" 
                  alt="Professional photography - Drone Aerial"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300"></div>
              </div>
              <div className="group relative overflow-hidden rounded-2xl aspect-[3/4] col-span-1 row-span-1">
                <img 
                  src="https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/ebgazyzp_593215234_1277152031119158_7645966311573582184_n.jpg" 
                  alt="Professional photography - Event"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-24 bg-[#0a0a0a] text-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-medium mb-6">
                  Quality. Strategy. <span className="text-orange-400">Consistency.</span>
                </h2>
                <p className="text-gray-400 text-lg mb-8">
                  Anyone can take a photo. Not everyone can create visuals that perform.
                </p>
                <div className="mb-8">
                  <p className="text-white font-medium mb-4">What sets us apart:</p>
                  <ul className="space-y-3">
                    {whyChooseUs.map((item, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <Check size={18} className="text-orange-400 flex-shrink-0" />
                        <span className="text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-xl text-white font-medium">
                  Your visuals should work as hard as you do.
                </p>
              </div>
              <div className="relative">
                <div className="bg-gradient-to-br from-orange-500/20 via-rose-500/20 to-purple-500/20 rounded-3xl p-8">
                  <div className="aspect-video rounded-xl overflow-hidden shadow-2xl">
                    <iframe
                      width="100%"
                      height="100%"
                      src="https://www.youtube.com/embed/ZHlRDi0t_PE"
                      title="Mobyle Videography Showcase"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      className="w-full h-full"
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Process */}
        <section className="py-24 bg-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                Our Creative Process
              </h2>
            </div>

            <div className="grid md:grid-cols-5 gap-8">
              {process.map((item, index) => (
                <div key={index} className="relative">
                  <span className="text-6xl font-light text-gray-200">{item.step}</span>
                  <h3 className="text-lg font-medium text-black mt-2 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                  {index < process.length - 1 && (
                    <div className="hidden md:block absolute top-8 right-0 w-full h-px bg-gray-200 -mr-4"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Where Content Works */}
        <section className="py-24 bg-[#f8f7f4]">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                Where Your Content Works Best
              </h2>
              <p className="text-lg text-gray-600">
                Our visuals are designed to perform across:
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {whereContentWorks.map((item, index) => (
                <div key={index} className="bg-white rounded-full px-6 py-3 flex items-center gap-3 shadow-sm">
                  <item.icon className="w-5 h-5 text-orange-500" />
                  <span className="text-gray-700 font-medium">{item.label}</span>
                </div>
              ))}
            </div>

            <p className="text-center text-2xl text-black font-medium">
              One shoot. Endless use.
            </p>
          </div>
        </section>

        {/* Results */}
        <section className="py-24 bg-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-12">
                Results You Can Expect
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {results.map((result, index) => (
                  <div key={index} className="bg-gray-50 rounded-2xl p-6 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                      <Check size={20} className="text-orange-600" />
                    </div>
                    <p className="text-gray-800 font-medium text-left">{result}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="contact" className="py-24 bg-[#0a0a0a] text-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-light mb-6">
                Ready to Elevate Your Brand{' '}
                <span className="font-medium">With Professional Visuals?</span>
              </h2>
              <p className="text-gray-400 text-lg mb-8">
                Let's create photo and video content that makes your brand impossible to ignore.
              </p>
              <a
                href="mailto:hello@gomobyle.com"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-black rounded-full font-medium hover:bg-gray-200 transition-colors mb-4"
              >
                Book a Free Creative Consultation <ArrowRight size={18} />
              </a>
              <p className="text-gray-500 text-sm">
                No pressure. Just clarity and a clear creative plan.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <CookieBanner />
    </div>
  );
};

export default MediaProduction;
